import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { ticketPreprocessingService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import type { TicketPreprocessingConfig } from '@/types';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.
const ticketPreprocessingQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'ticket-preprocessing', buScopeKey(get)],
  queryFn: () => ticketPreprocessingService.getTicketPreprocessingConfig(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

const updateTicketPreprocessingMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'ticket-preprocessing', 'update'],
  mutationFn: (payload: TicketPreprocessingConfig) =>
    ticketPreprocessingService.updateTicketPreprocessingConfig(payload, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['brand', 'ticket-preprocessing']);
  }
}));

export function useTicketPreprocessingConfig() {
  const [query] = useAtom(ticketPreprocessingQueryAtom);
  return query;
}

export function useUpdateTicketPreprocessingConfig() {
  const [mutation] = useAtom(updateTicketPreprocessingMutationAtom);
  return mutation;
}
